import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fmtDate, fmtDateTime, todayIso } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/StatusBadge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Calendar, Plus, CheckCircle2, X, Trash2, MapPin, Clock } from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  planned: { label: "Planlandı", variant: "info" },
  visited: { label: "Ziyaret Edildi", variant: "success" },
  cancelled: { label: "İptal", variant: "default" },
  missed: { label: "Kaçırıldı", variant: "error" },
};

export default function WorkPlans() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [plans, setPlans] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [filterUser, setFilterUser] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [showNew, setShowNew] = useState(false);
  const [showComplete, setShowComplete] = useState(null);
  const [visitNotes, setVisitNotes] = useState("");
  const [form, setForm] = useState({
    company_id: "", manual_company_name: "",
    plan_date: todayIso(), plan_time: "09:00",
    duration_minutes: 60, subject: "", notes: "",
  });

  const load = async () => {
    const params = {};
    if (isAdmin && filterUser !== "all") params.user_id = filterUser;
    if (filterStatus !== "all") params.status = filterStatus;
    const { data } = await api.get("/work-plans", { params });
    setPlans(data);
  };
  useEffect(() => {
    api.get("/companies").then((r) => setCompanies(r.data));
    if (isAdmin) api.get("/users").then((r) => setUsers(r.data));
    load();
  }, [isAdmin, filterUser, filterStatus]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.subject.trim()) return toast.error("Konu gerekli");
    if (!form.company_id && !form.manual_company_name) return toast.error("Firma seçin veya yazın");
    const plan_datetime = new Date(`${form.plan_date}T${form.plan_time}:00`).toISOString();
    try {
      await api.post("/work-plans", {
        company_id: form.company_id || null,
        manual_company_name: form.manual_company_name,
        plan_datetime,
        duration_minutes: Number(form.duration_minutes),
        subject: form.subject,
        notes: form.notes,
      });
      toast.success("İş planı oluşturuldu. 2 saat öncesinde hatırlatma alacaksınız.");
      setShowNew(false);
      setForm({ company_id: "", manual_company_name: "", plan_date: todayIso(),
                plan_time: "09:00", duration_minutes: 60, subject: "", notes: "" });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Hata"); }
  };

  const complete = async () => {
    if (!visitNotes.trim()) return toast.error("Ziyaret notunu yazın");
    try {
      await api.patch(`/work-plans/${showComplete.id}/complete`, { visit_notes: visitNotes });
      toast.success("Ziyaret tamamlandı olarak işaretlendi");
      setShowComplete(null); setVisitNotes(""); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Hata"); }
  };

  const cancel = async (id) => {
    if (!window.confirm("İptal edilsin mi?")) return;
    await api.patch(`/work-plans/${id}/cancel`); toast.success("İptal edildi"); load();
  };
  const remove = async (id) => {
    if (!window.confirm("Kalıcı olarak silinsin mi?")) return;
    await api.delete(`/work-plans/${id}`); toast.success("Silindi"); load();
  };

  // Group by day for weekly display
  const groups = {};
  plans.forEach((p) => {
    const day = String(p.plan_datetime).slice(0, 10);
    if (!groups[day]) groups[day] = [];
    groups[day].push(p);
  });
  const sortedDays = Object.keys(groups).sort();

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3 justify-between">
        <div className="flex flex-wrap gap-3">
          {isAdmin && (
            <div>
              <label className="block text-xs font-semibold mb-1.5">Personel</label>
              <Select value={filterUser} onValueChange={setFilterUser}>
                <SelectTrigger className="min-w-[180px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tüm Personeller</SelectItem>
                  {users.filter(u => u.role !== "admin").map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <label className="block text-xs font-semibold mb-1.5">Durum</label>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="min-w-[160px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tümü</SelectItem>
                <SelectItem value="planned">Planlandı</SelectItem>
                <SelectItem value="visited">Ziyaret Edildi</SelectItem>
                <SelectItem value="cancelled">İptal</SelectItem>
                <SelectItem value="missed">Kaçırıldı</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <Button onClick={() => setShowNew(true)} data-testid="new-plan-btn"
                className="bg-[#0B3676] hover:bg-[#08285A] text-white">
          <Plus size={16} className="mr-1" /> Yeni İş Planı
        </Button>
      </div>

      {sortedDays.length === 0 ? (
        <div className="azd-card p-8 text-center text-[#6B7280]">
          İş planı bulunmuyor. "Yeni İş Planı" ile başlayın.
        </div>
      ) : sortedDays.map((day) => (
        <div key={day} className="azd-card p-5">
          <div className="flex items-center gap-2 mb-3 pb-3 border-b border-[#E5E7EB]">
            <Calendar size={18} className="text-[#0B3676]" />
            <div className="font-extrabold text-[#08285A]">{fmtDate(day)}</div>
            <Badge variant="info">{groups[day].length} planlanmış</Badge>
          </div>
          <div className="space-y-2">
            {groups[day].sort((a,b) => a.plan_datetime.localeCompare(b.plan_datetime)).map((p) => {
              const dt = new Date(p.plan_datetime);
              const timeStr = `${String(dt.getHours()).padStart(2,'0')}:${String(dt.getMinutes()).padStart(2,'0')}`;
              return (
                <div key={p.id} data-testid={`plan-${p.id}`}
                     className="flex items-start gap-3 p-3 rounded-lg border border-[#E5E7EB] hover:bg-[#F6F8FB]/50">
                  <div className="w-16 text-center bg-[#08285A] text-white rounded-lg py-2 shrink-0">
                    <div className="text-xs opacity-70">Saat</div>
                    <div className="font-bold">{timeStr}</div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-bold text-[#08285A]">{p.company_name}</div>
                      <Badge variant={STATUS[p.status]?.variant}>{STATUS[p.status]?.label}</Badge>
                      {isAdmin && <span className="text-xs text-[#6B7280]">• {p.user_name}</span>}
                    </div>
                    <div className="text-sm text-[#111827] mt-1">{p.subject}</div>
                    <div className="flex gap-3 mt-1 text-xs text-[#6B7280]">
                      <span className="flex items-center gap-1"><Clock size={10} /> {p.duration_minutes} dk</span>
                    </div>
                    {p.notes && (
                      <div className="mt-2 text-xs text-[#6B7280] p-2 bg-[#F6F8FB] rounded">
                        📝 {p.notes}
                      </div>
                    )}
                    {p.visit_notes && (
                      <div className="mt-2 text-xs p-2 bg-[#16A34A]/5 border-l-2 border-[#16A34A] rounded">
                        <div className="font-bold text-[#16A34A] mb-1">
                          ✓ Ziyaret Notları ({fmtDateTime(p.visited_at)}):
                        </div>
                        <div className="text-[#111827] whitespace-pre-line">{p.visit_notes}</div>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-1 shrink-0">
                    {p.status === "planned" && (p.user_id === user?.id || isAdmin) && (
                      <button onClick={() => { setShowComplete(p); setVisitNotes(""); }}
                              data-testid={`complete-${p.id}`}
                              className="p-1.5 text-[#16A34A] hover:bg-[#16A34A]/10 rounded" title="Ziyaret Edildi">
                        <CheckCircle2 size={16} />
                      </button>
                    )}
                    {p.status === "planned" && (p.user_id === user?.id || isAdmin) && (
                      <button onClick={() => cancel(p.id)}
                              className="p-1.5 text-[#6B7280] hover:text-[#F59E0B] hover:bg-[#F59E0B]/10 rounded" title="İptal">
                        <X size={16} />
                      </button>
                    )}
                    {(p.user_id === user?.id || isAdmin) && (
                      <button onClick={() => remove(p.id)}
                              className="p-1.5 text-[#6B7280] hover:text-[#DC2626] hover:bg-[#DC2626]/10 rounded" title="Sil">
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* New plan dialog */}
      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Yeni İş Planı</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="grid md:grid-cols-2 gap-3 py-2">
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold mb-1.5">Firma <span className="text-[#DC2626]">*</span></label>
              <Select value={form.company_id || "manual"}
                      onValueChange={(v) => setForm({...form, company_id: v === "manual" ? "" : v})}>
                <SelectTrigger data-testid="plan-company"><SelectValue placeholder="Firma seçin" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">— Manuel Firma Adı —</SelectItem>
                  {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}
                </SelectContent>
              </Select>
              {!form.company_id && (
                <Input className="mt-2" placeholder="Manuel firma adı"
                       value={form.manual_company_name}
                       onChange={(e) => setForm({...form, manual_company_name: e.target.value})} />
              )}
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1.5">Tarih *</label>
              <Input type="date" value={form.plan_date}
                     onChange={(e) => setForm({...form, plan_date: e.target.value})} required />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1.5">Saat *</label>
              <Input type="time" value={form.plan_time}
                     onChange={(e) => setForm({...form, plan_time: e.target.value})} required data-testid="plan-time" />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1.5">Süre (dk)</label>
              <Input type="number" min="15" step="15" value={form.duration_minutes}
                     onChange={(e) => setForm({...form, duration_minutes: e.target.value})} />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold mb-1.5">Konu / Amaç *</label>
              <Input value={form.subject} onChange={(e) => setForm({...form, subject: e.target.value})}
                     placeholder="Örn: Ürün tanıtımı, teklif görüşmesi, tahsilat"
                     required data-testid="plan-subject" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold mb-1.5">Notlar (opsiyonel)</label>
              <Textarea value={form.notes} rows={3}
                        onChange={(e) => setForm({...form, notes: e.target.value})}
                        placeholder="Ziyaret öncesi hazırlık, sorulacaklar vs." />
            </div>
            <DialogFooter className="md:col-span-2">
              <Button type="button" variant="outline" onClick={() => setShowNew(false)}>Vazgeç</Button>
              <Button type="submit" data-testid="save-plan"
                      className="bg-[#0B3676] hover:bg-[#08285A] text-white">Kaydet</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Complete visit dialog */}
      <Dialog open={!!showComplete} onOpenChange={(o) => !o && setShowComplete(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Ziyaret Tamamlandı</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="text-sm">
              <div className="font-bold text-[#08285A]">{showComplete?.company_name}</div>
              <div className="text-xs text-[#6B7280]">{showComplete?.subject}</div>
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1.5">Ne Yapıldı? *</label>
              <Textarea value={visitNotes} onChange={(e) => setVisitNotes(e.target.value)}
                        rows={6} placeholder="Ziyarette neler konuşuldu, hangi kararlar alındı..."
                        data-testid="visit-notes" required />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowComplete(null)}>Vazgeç</Button>
            <Button onClick={complete} data-testid="save-visit"
                    className="bg-[#16A34A] hover:bg-[#15803D] text-white">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
